import {view, Divider, tokens, Icon} from '@sail/ui';
import {refund as refundIcon} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useState} from 'react';
import {useWidget} from '../../../Widget/useWidget';
import {RefundCardStatusBadge} from './StatusBadges/RefundCardStatusBadge';

type RefundCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.refund_card.title',
    description: 'Refund Card title',
    defaultMessage: 'Refund',
  },
});

export const RefundCard = ({agentCallResponse}: RefundCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const [isHovered, setIsHovered] = useState(false);

  const {refund, customer, charge} = agentCallResponse;
  if (!refund) {
    return null;
  }

  const {amount, currency, status} = refund || {};
  const {name: customerName, email: customerEmail} = customer || {};
  const {id: chargeId} = charge || {};

  const displayName = () => {
    if (customerName) {
      return customerName;
    }
    if (customerEmail) {
      return customerEmail;
    }
  };

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/payments/${chargeId}`,
          },
        });
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'xsmall',
          width: '100%',
          minWidth: 0,
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: tokens.space[75].toString(),
            alignItems: 'center',
          }}
        >
          <Icon
            icon={refundIcon}
            size="xsmall"
            css={{color: isHovered ? 'action.primary' : 'subdued'}}
          />
          <view.span
            css={{
              color: 'subdued',
              font: 'label.small.emphasized',
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          {displayName() ? (
            <view.div css={{height: 10, stack: 'x', width: 0}}>
              <Divider />
            </view.div>
          ) : null}
          {displayName() ? (
            <view.span
              css={{
                font: 'label.small.emphasized',
                color: 'action.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ':hover': {
                  color: 'action.primary.hovered',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                widget.requestAction({
                  name: 'navigate',
                  data: {
                    href: `/customers/${customer?.id}`,
                  },
                });
              }}
            >
              {displayName()}
            </view.span>
          ) : null}
        </view.div>
        <view.div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'small',
            width: '100%',
            minWidth: 0,
          }}
        >
          {amount && currency ? (
            <view.span
              css={{
                font: 'heading.large',
                flexShrink: 0,
              }}
            >
              {intl.formatNumber(amount / 100, {
                style: 'currency',
                currency,
              })}
            </view.span>
          ) : null}
          <view.div
            css={{
              minWidth: 0,
              flex: 1,
              marginTop: 'xxsmall',
            }}
          >
            <RefundCardStatusBadge status={status} />
          </view.div>
        </view.div>
      </view.div>
    </view.div>
  );
};
